import { useState, useMemo } from 'react';
import { Loader2, FolderHeart, BellOff } from 'lucide-react';
import { useCollectionFollows } from '@/hooks/useCollectionFollows';
import { useMultipleCollectionFollowerProfiles } from '@/hooks/useCollectionFollowerProfiles';
import { useAuth } from '@/hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Collection } from '@/hooks/useCollections';
import { CollectionGridCard } from './CollectionGridCard';
import { CollectionGridSkeleton } from '@/components/skeletons';
import { DraggableBottomSheet } from '@/components/ui/DraggableBottomSheet';
import { toast } from 'sonner';

interface FollowedCollectionsSectionProps {
  onSelectCollection: (collection: any) => void;
  showHeader?: boolean;
}

export function FollowedCollectionsSection({ onSelectCollection, showHeader = true }: FollowedCollectionsSectionProps) {
  const { user } = useAuth();
  const { followedCollections, isLoading: isLoadingFollows, toggleFollow } = useCollectionFollows();
  const [longPressCollection, setLongPressCollection] = useState<Collection | null>(null);
  const [unfollowingId, setUnfollowingId] = useState<string | null>(null);
  
  // Get IDs of followed collections
  const followedCollectionIds = useMemo(() => 
    followedCollections.map(f => f.collection_id),
    [followedCollections]
  );
  
  // Fetch follower profiles for all followed collections
  const { followersMap } = useMultipleCollectionFollowerProfiles(followedCollectionIds);
  

  // Fetch full collection details with owner info using optimized view
  const { data: collectionsWithDetails = [], isLoading: isLoadingDetails } = useQuery({
    queryKey: ['followed-collections-details', followedCollections.map(f => f.collection_id)],
    queryFn: async () => {
      if (!followedCollections.length) return [];

      const collectionIds = followedCollections.map(f => f.collection_id);

      // Use the optimized view that already has item_count, preview_images computed
      const { data: collections, error } = await supabase
        .from('collections_with_stats' as any)
        .select('*')
        .in('id', collectionIds);

      if (error) throw error;

      // Fetch owner profiles in a single batch query
      const ownerIds = [...new Set(collections?.map((c: any) => c.user_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', ownerIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]) || []);

      // Transform to match Collection interface
      return (collections || []).map((c: any) => {
        const profile = profileMap.get(c.user_id);
        return {
          ...c,
          preview_images: c.preview_images || [],
          owner_name: profile?.display_name || 'Unknown',
          owner_avatar: profile?.avatar_url,
        } as Collection & { owner_name?: string; owner_avatar?: string };
      });
    },
    enabled: followedCollections.length > 0,
  });

  const handleUnfollow = async (collection: Collection) => {
    setUnfollowingId(collection.id);
    try {
      await toggleFollow(collection.id);
      // Unfollowed silently
    } catch (error) {
      toast.error('Failed to unfollow collection');
    } finally {
      setUnfollowingId(null);
      setLongPressCollection(null);
    }
  };

  const isLoading = isLoadingFollows || isLoadingDetails;

  if (!user) return null;
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        {showHeader && (
          <div className="flex items-center gap-2">
            <FolderHeart className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-muted-foreground">FOLLOWING</h3>
          </div>
        )}
        <CollectionGridSkeleton count={4} showCreateCard={false} />
      </div>
    );
  }

  if (followedCollections.length === 0) {
    return (
      <div className="text-center py-8">
        <FolderHeart className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
        <p className="font-medium text-foreground">Not following any collections</p>
        <p className="text-sm text-muted-foreground mt-1">
          Follow collections from other users to see them here
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-3">
        {showHeader && (
          <div className="flex items-center gap-2">
            <FolderHeart className="w-4 h-4 text-primary" />
            <h3 className="text-sm font-semibold text-muted-foreground">FOLLOWING</h3>
            <span className="text-xs text-muted-foreground">({collectionsWithDetails.length})</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          {collectionsWithDetails.map((collection) => (
            <CollectionGridCard
              key={collection.id}
              collection={collection}
              onSelect={onSelectCollection}
              onLongPress={setLongPressCollection}
              showOwner={true}
              followers={followersMap[collection.id] || []}
            />
          ))}
        </div>
      </div>

      {/* Long Press Actions Sheet - Only unfollow option */}
      <DraggableBottomSheet
        open={!!longPressCollection}
        onOpenChange={(open) => !open && setLongPressCollection(null)}
        title={longPressCollection?.name || 'Collection Options'}
        height="auto"
      >
        {longPressCollection && (
          <div className="pb-6">
            <button
              onClick={() => handleUnfollow(longPressCollection)}
              disabled={unfollowingId === longPressCollection.id}
              className="w-full flex items-center gap-3 p-4 rounded-xl active:bg-destructive/10 transition-colors disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                {unfollowingId === longPressCollection.id ? (
                  <Loader2 className="w-5 h-5 text-destructive animate-spin" />
                ) : (
                  <BellOff className="w-5 h-5 text-destructive" />
                )}
              </div>
              <div className="text-left">
                <p className="font-medium text-destructive">Unfollow Collection</p>
                <p className="text-sm text-muted-foreground">Stop receiving updates</p>
              </div>
            </button>
          </div>
        )}
      </DraggableBottomSheet>
    </>
  );
}
